import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../../../../shared/components/card/Card";
import { Button } from "../../../../../../shared/components/button/Button";
import {
  BaseSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../../shared/components/select/BaseSelect";
import { Plus, Trash2, X } from "lucide-react";
import { Badge } from "../../../../../../shared/components/badge/Badge";
import { Input } from "../../../../../../shared/components/input/Input";
import SearchableSelect from "./SearchableSelect";
import useAxios from "../../../../../../hooks/useAxios";
import { API_DATASET_DIMENSION_VALUES } from "../../../../../../shared/lib/apiEndpointsResolver";
import { catchFunction } from "../../../../../../shared/lib/utils";
import CustomDatePicker from "./CustomDatePicker";
import useLoading from "../../../../../../shared/components/loading/useLoading";

const filterOperations = [
  {
    id: "equals",
    name: "Equals",
    code: "eq",
    symbol: "=",
    applicableTypes: ["varchar"],
  },
  {
    id: "not_equals",
    name: "Not Equals",
    code: "neq",
    symbol: "≠",
    applicableTypes: ["varchar"],
  },
  {
    id: "in",
    name: "Is Any",
    code: "in",
    symbol: "*",
    applicableTypes: ["varchar"],
  },
  {
    id: "contains",
    name: "Contains",
    code: "ilike",
    symbol: "∋",
    applicableTypes: ["varchar"],
  },
  {
    id: "greater_than",
    name: "Greater Than",
    code: "gt",
    symbol: ">",
    applicableTypes: ["date"],
  },
  {
    id: "less_than",
    name: "Less Than",
    code: "lt",
    symbol: "<",
    applicableTypes: ["date"],
  },
  {
    id: "greater_equal",
    name: "Greater or Equal",
    code: "gte",
    symbol: "≥",
    applicableTypes: ["date"],
  },
  {
    id: "less_equal",
    name: "Less or Equal",
    code: "lte",
    symbol: "≤",
    applicableTypes: ["date"],
  },
  {
    id: "between",
    name: "Between",
    code: "between",
    symbol: "⟷",
    applicableTypes: ["date"],
  },
];

const FilterBuilder = ({
  datasetCode,
  dimensions,
  filters,
  onFiltersChange,
}) => {
  const [selectedDimension, setSelectedDimension] = useState(null);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [filterValue, setFilterValue] = useState("");
  const [endFilterValue, setEndFilterValue] = useState("");
  const [selectedValues, setSelectedValues] = useState([]);
  // *********************************************
  const axios = useAxios();
  const { setLoading } = useLoading();

  const availableOperations = selectedDimension
    ? filterOperations.filter((op) =>
        op.applicableTypes.includes(selectedDimension.dimensionType.code),
      )
    : [];

  const isDateDimension = selectedDimension?.dimensionType?.code === "date";
  const isRangeOperation = selectedOperation?.id === "between";
  const isPresetOperation = ["this_week", "this_month"].includes(
    selectedOperation?.id || "",
  );

  const addFilter = () => {
    if (selectedDimension && selectedOperation) {
      if (
        selectedDimension.dimensionType.code === "varchar" &&
        selectedValues.length > 0
      ) {
        const newFilters = selectedValues.map((value) => ({
          id: `${Date.now()}-${Math.random()}`,
          dimension: selectedDimension,
          operation: selectedOperation,
          value: value,
        }));

        onFiltersChange([...filters, ...newFilters]);

        setSelectedDimension(null);
        setSelectedOperation(null);
        setFilterValue("");
        setEndFilterValue("");
        setSelectedValues([]);
        return;
      }

      if (
        isPresetOperation ||
        (filterValue && (!isRangeOperation || endFilterValue))
      ) {
        const newFilter = {
          id: Date.now().toString(),
          dimension: selectedDimension,
          operation: selectedOperation,
          value:
            selectedOperation.id === "contains"
              ? `%${filterValue}%`
              : filterValue,
          endValue: isRangeOperation ? endFilterValue : undefined,
        };

        console.log("between filter");
        console.log(newFilter);
        onFiltersChange([...filters, newFilter]);

        setSelectedDimension(null);
        setSelectedOperation(null);
        setFilterValue("");
        setEndFilterValue("");
        setSelectedValues([]);
      }
    }
  };

  const removeFilter = (filterId) => {
    onFiltersChange(filters.filter((f) => f.id !== filterId));
  };

  const clearAllFilters = () => {
    onFiltersChange([]);
  };

  const renderValueInput = () => {
    if (isPresetOperation) {
      return (
        <div className="flex items-center justify-center h-10 text-sm text-muted-foreground border rounded-md">
          No value needed
        </div>
      );
    }

    if (selectedOperation?.id === "last_days") {
      return (
        <Input
          type="number"
          placeholder="Number of days"
          value={filterValue}
          onChange={(e) => setFilterValue(e.target.value)}
          disabled={!selectedOperation}
          min="1"
        />
      );
    }

    if (isDateDimension) {
      const min = selectedDimension?.possibleValues?.reduce(
        (min, c) => (c < min ? c : min),
        "9999-12-31",
      );
      const max = selectedDimension?.possibleValues?.reduce(
        (max, c) => (c > max ? c : max),
        "",
      );

      if (isRangeOperation) {
        return (
          <div className="flex flex-row gap-x-2">
            <CustomDatePicker
              value={filterValue}
              onChange={setFilterValue}
              placeholder="Start date"
              disabled={!selectedOperation}
              min={min}
              max={max}
            />
            <CustomDatePicker
              value={endFilterValue}
              onChange={setEndFilterValue}
              placeholder="End date"
              disabled={!selectedOperation}
              min={min}
              max={max}
            />
          </div>
        );
      } else {
        return (
          <CustomDatePicker
            value={filterValue}
            onChange={setFilterValue}
            placeholder="Select date"
            disabled={!selectedOperation}
            min={min}
            max={max}
          />
        );
      }
    }

    if (
      selectedDimension?.dimensionType?.code === "varchar" &&
      (selectedOperation?.id === "equals" ||
        selectedOperation?.id === "not_equals") &&
      selectedDimension.possibleValues
    ) {
      return (
        <div className="relative">
          <SearchableSelect
            values={selectedDimension.possibleValues}
            selectedValues={selectedValues}
            onSelectionChange={setSelectedValues}
            placeholder="Search and select values..."
            disabled={!selectedOperation}
            singleSelection={true}
          />
        </div>
      );
    }

    if (
      selectedDimension?.dimensionType?.code === "varchar" &&
      selectedOperation?.id === "in" &&
      selectedDimension.possibleValues
    ) {
      return (
        <div className="relative">
          <SearchableSelect
            values={selectedDimension.possibleValues}
            selectedValues={selectedValues}
            onSelectionChange={setSelectedValues}
            placeholder="Search and select values..."
            disabled={!selectedOperation}
          />
        </div>
      );
    }

    if (
      selectedDimension?.dimensionType?.code === "varchar" &&
      selectedOperation?.id === "contains" &&
      selectedDimension.possibleValues
    ) {
      return (
        <Input
          placeholder="Enter value for partial searching"
          value={filterValue}
          onChange={(e) => setFilterValue(e.target.value)}
          disabled={!selectedOperation}
        />
      );
    }

    if (selectedDimension?.possibleValues && selectedOperation) {
      return (
        <BaseSelect value={filterValue} onValueChange={setFilterValue}>
          <SelectTrigger>
            <SelectValue placeholder="Select value" />
          </SelectTrigger>
          <SelectContent>
            {selectedDimension.possibleValues.map((value) => (
              <SelectItem key={value} value={value}>
                {value}
              </SelectItem>
            ))}
          </SelectContent>
        </BaseSelect>
      );
    }

    return (
      <Input
        placeholder="Enter value"
        value={filterValue}
        onChange={(e) => setFilterValue(e.target.value)}
        disabled={!selectedOperation}
      />
    );
  };

  const canAddFilter = () => {
    if (!selectedDimension || !selectedOperation) return false;
    if (isPresetOperation) return true;
    if (isRangeOperation) return filterValue && endFilterValue;

    if (
      selectedDimension?.dimensionType?.code === "varchar" &&
      selectedOperation?.id !== "contains"
    ) {
      return selectedValues.length > 0;
    }

    if (
      selectedDimension?.dimensionType?.code === "varchar" &&
      selectedOperation?.id === "contains"
    ) {
      return filterValue.length > 0;
    }

    return filterValue;
  };

  const formatFilterDisplay = (filter) => {
    if (filter.operation.id === "between" && filter.endValue) {
      return `${filter.dimension.name} ${filter.operation.symbol} ${filter.value} and ${filter.endValue}`;
    }
    if (["this_week", "this_month"].includes(filter.operation.id)) {
      return `${filter.dimension.name} ${filter.operation.name}`;
    }
    if (filter.operation.id === "last_days") {
      return `${filter.dimension.name} Last ${filter.value} Days`;
    }
    return `${filter.dimension.name} ${filter.operation.symbol} ${filter.value}`;
  };

  const onDimensionSelectionValueChange = (value) => {
    const dimension = dimensions.find((d) => d.code === value);
    setSelectedDimension(dimension || null);
    setSelectedOperation(null);
    setFilterValue("");
    setEndFilterValue("");
    setSelectedValues([]);

    if (dimension.possibleValues.length === 0) {
      setLoading(true);
      axios
        .get(API_DATASET_DIMENSION_VALUES(datasetCode, dimension?.code))
        .then((response) => {
          const data = response.data;
          if (data.length >= 0) {
            const key = Object.keys(data[0])[0];
            dimension.possibleValues = data.map((item) => item[key]);
            setSelectedDimension(dimension || null);
          }
        })
        .catch(catchFunction)
        .finally(() => setLoading(false));
    } else {
      setSelectedDimension(dimension || null);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Filters</CardTitle>
        {filters.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllFilters}
            className="text-muted-foreground"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Clear All ({filters.length})
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Dimension</label>
            <BaseSelect
              value={selectedDimension?.code || ""}
              onValueChange={onDimensionSelectionValueChange}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select dimension" />
              </SelectTrigger>
              <SelectContent>
                {dimensions.map((dimension) => (
                  <SelectItem key={dimension.code} value={dimension.code}>
                    {dimension.name}
                    <span className="ml-2 text-xs text-muted-foreground">
                      ({dimension.dimensionType.name})
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </BaseSelect>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Operation</label>
            <BaseSelect
              value={selectedOperation?.id || ""}
              onValueChange={(value) => {
                const operation = availableOperations.find(
                  (op) => op.id === value,
                );
                setSelectedOperation(operation || null);
                setFilterValue("");
                setEndFilterValue("");
                setSelectedValues([]);
              }}
              disabled={!selectedDimension}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select operation" />
              </SelectTrigger>
              <SelectContent>
                {availableOperations.map((operation) => (
                  <SelectItem key={operation.id} value={operation.id}>
                    {operation.name} ({operation.symbol})
                  </SelectItem>
                ))}
              </SelectContent>
            </BaseSelect>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Value</label>
            {renderValueInput()}
          </div>

          <div className="flex items-end">
            <Button
              onClick={addFilter}
              disabled={!canAddFilter()}
              className="w-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Filter
            </Button>
          </div>
        </div>

        {filters.length > 0 && (
          <div>
            <h4 className="text-sm font-medium mb-2">Applied Filters:</h4>
            <div className="flex flex-wrap gap-2">
              {filters.map((filter) => (
                <Badge
                  key={filter.id}
                  variant="secondary"
                  className="flex items-center gap-2"
                >
                  {formatFilterDisplay(filter)}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0 hover:bg-destructive hover:text-destructive-foreground"
                    onClick={() => removeFilter(filter.id)}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FilterBuilder;
